import React from 'react';

import {
  CmBox,
  CmButtonDatePicker,
  CmCard,
  CmCheckBox,
  CmFlex,
  CmFormControl,
  CmFormErrorMessage,
  CmFormLabel,
  CmLabelAndSelect,
  CmRadio,
  CmRadioGroup,
  CmScrollBox,
  CmSelectDigitsTime,
  CmSimpleGrid,
  CmStack,
  CmText,
} from '@/components/uiParts';
import { formState } from '@/pages/camel-order/temp-hour/hooks/useTempHoursForm';
import {
  tempHoursForm,
  tempHourTypeToLabel,
} from '@/pages/camel-order/temp-hour/service';

type Props = {
  height: string;
} & formState;

const convertToDateTime = (timeString: string): Date => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return new Date(0, 0, 0, hours, minutes, 0, 0);
};

export const TempHourForm: React.FC<Props> = ({
  height,
  sites,
  deliveryStores,
  selectedSiteId,
  tempHourDate,
  tempHourType,
  tempHourStartHour,
  tempHourStartMinute,
  tempHourEndHour,
  tempHourEndMinute,
  deliveryStoreCheckboxState,
  handleChangeSelectedSiteId,
  handleChangeTempHourDate,
  handleChangeTempHourType,
  handleChangeTempHourStartHour,
  handleChangeTempHourStartMinute,
  handleChangeTempHourEndHour,
  handleChangeTempHourEndMinute,
  handleChangeDeliveryStoreCheckbox,
  handleChangeAllDeliveryStoreCheckbox,
  isAllDeliveryStoresChecked,
  error,
}): JSX.Element => {
  const siteUrlOptions = sites.map((site) => ({
    id: site.id,
    url: site.url,
  }));

  return (
    <CmCard mt="25px" height={height} padding={'20px 40px 10px 36px'}>
      <CmStack pl="4px">
        <CmFlex justify="space-between">
          <CmFlex alignItems="center" p="0 0 10px">
            <CmLabelAndSelect
              id={tempHoursForm.siteUrl.key}
              label={tempHoursForm.siteUrl.label}
              width="300px"
              fontWeight="bold"
              value={selectedSiteId}
              onChange={(e) => {
                handleChangeSelectedSiteId(e.currentTarget.value);
              }}
            >
              {siteUrlOptions.map((siteUrlOption) => (
                <option key={siteUrlOption.id} value={siteUrlOption.id}>
                  {siteUrlOption.url}
                </option>
              ))}
            </CmLabelAndSelect>
          </CmFlex>
        </CmFlex>
      </CmStack>
      <CmScrollBox pl="4px">
        <CmStack spacing="20px" alignItems="flex-start">
          <CmFormControl>
            <CmFormLabel isRequired fontWeight="bold">
              日付
            </CmFormLabel>
            <CmFlex alignItems="center">
              <CmButtonDatePicker
                selectedDate={tempHourDate}
                onChange={handleChangeTempHourDate}
                dateFormat={'yyyy - MM - dd'}
                minDate={new Date()}
              />
            </CmFlex>
          </CmFormControl>

          <CmBox>
            <CmFormLabel isRequired fontWeight="bold">
              営業方法
            </CmFormLabel>
            <CmRadioGroup
              value={tempHourType}
              onChange={handleChangeTempHourType}
            >
              <CmFlex direction="column" alignItems="flex-start">
                <CmRadio size="md" value="close">
                  {tempHourTypeToLabel.close}
                </CmRadio>
                <CmRadio size="md" value="open" mt="10px">
                  {tempHourTypeToLabel.open}
                </CmRadio>
              </CmFlex>
            </CmRadioGroup>

            {tempHourType === 'open' && (
              <CmBox mt="10px" pl="23px" display="flex">
                <CmCard display="flex" flexDir="column">
                  <CmFormControl isInvalid={error.tempHourDate.isError}>
                    <CmFlex direction="row" alignItems="flex-end">
                      <CmBox>
                        <CmFormLabel fontWeight="bold">開始時間</CmFormLabel>
                        <CmSelectDigitsTime
                          date={convertToDateTime(
                            `${tempHourStartHour}:${tempHourStartMinute}`
                          )}
                          handleChangeHour={handleChangeTempHourStartHour}
                          handleChangeMinutes={handleChangeTempHourStartMinute}
                        />
                      </CmBox>
                      <CmText mx="20px" height="35px">
                        〜
                      </CmText>
                      <CmBox>
                        <CmFormLabel fontWeight="bold">終了時間</CmFormLabel>
                        <CmSelectDigitsTime
                          date={convertToDateTime(
                            `${tempHourEndHour}:${tempHourEndMinute}`
                          )}
                          handleChangeHour={handleChangeTempHourEndHour}
                          handleChangeMinutes={handleChangeTempHourEndMinute}
                        />
                      </CmBox>
                    </CmFlex>
                    <CmFormErrorMessage>
                      {error.tempHourDate.message}
                    </CmFormErrorMessage>
                  </CmFormControl>
                </CmCard>
              </CmBox>
            )}
          </CmBox>

          <CmBox>
            <CmFormLabel isRequired fontWeight="bold">
              店舗を選択
            </CmFormLabel>
            <CmCheckBox
              isChecked={isAllDeliveryStoresChecked}
              onChange={handleChangeAllDeliveryStoreCheckbox}
            >
              全ての店舗を選択
            </CmCheckBox>
            <CmSimpleGrid columns={2} spacing="10px" mt="20px">
              {deliveryStores.map((deliveryStore) => (
                <CmCheckBox
                  key={deliveryStore.id}
                  isChecked={deliveryStoreCheckboxState[deliveryStore.id]}
                  onChange={() =>
                    handleChangeDeliveryStoreCheckbox(deliveryStore.id)
                  }
                >
                  {deliveryStore.name}
                </CmCheckBox>
              ))}
            </CmSimpleGrid>
          </CmBox>
        </CmStack>
      </CmScrollBox>
    </CmCard>
  );
};
